/**
 * API utility functions
 * 
 * Centralized API configuration and request handlers.
 * Uses environment variables for full portability across environments.
 */

import axios from 'axios';

// API Base URL from environment - with fallback to current origin
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${API_BASE_URL}/api`;

/**
 * Get authentication headers
 * Includes JWT token from localStorage
 */
const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  
  const token = localStorage.getItem('jwt_token');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * Construct a URL for uploaded files
 * Handles both GridFS files (new) and legacy filesystem files
 * 
 * File path formats:
 * - GridFS: "gridfs:60a1234567890abcdef12345" -> /api/files/60a1234567890abcdef12345?token=<jwt>
 * - Legacy: "/app/backend/uploads/abc123.jpg" -> /api/uploads/abc123.jpg
 * 
 * @param {string} filePath - The file path from database
 * @returns {string|null} - Full URL to the file or null if invalid
 */
const getUploadUrl = (filePath) => {
  if (!filePath) return null;

  // Already an absolute URL — use as-is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Already a qualified API path (e.g. /api/profile/photo/{id}) — just prefix the host
  if (filePath.startsWith('/api/')) {
    return `${API_BASE_URL}${filePath}`;
  }

  // Check if it's a GridFS file (new storage format)
  if (filePath.startsWith('gridfs:')) {
    const fileId = filePath.replace('gridfs:', '');
    const token = localStorage.getItem('jwt_token');
    // Include token as query param for browser-based file loading (img src, etc.)
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/api/files/${fileId}${tokenParam}`;
  }
  
  // Legacy: Extract just the filename from the full path
  const filename = filePath.split('/').pop();
  if (!filename) return null;
  
  // Use API_BASE_URL which falls back to current origin
  return `${API_BASE_URL}/api/uploads/${filename}`;
};

/**
 * API client with auth support
 */
const apiClient = axios.create({
  baseURL: API,
});

// Add request interceptor to include auth header
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('jwt_token');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?reason=session_expired';
      }
    }
    return Promise.reject(error);
  }
);

export { API, API_BASE_URL, apiClient, getAuthHeaders, getUploadUrl };
export default apiClient;